import { Component, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, defer, EMPTY, Subscription } from 'rxjs';
import { arr_count, arr_first, blank, data_get, filled, observe, OptionalValue, spf } from '../../../../../shared/utils/common';
import { ViewedRecordDetails, ViewService } from '../../../../../services/view.service';
import { AssetActualFault } from '../../../types';
import { AssetService } from '../../../../../services/asset.service';
import { filter } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'assets-view-defects-button',
  template: `
    <fieldmagic-primary-button
      icon="exclamation-circle"
      [disabled]="fetching$ | async"
      [label]="(details$ | async) | data_get: 'label'"
      [color]="(details$ | async) | data_get: 'color'"
      tooltip="Click to view defects"
      (click)="onView()"
    >
    </fieldmagic-primary-button>
  `
})
export class ViewAssetDefectsButtonComponent implements OnInit, OnDestroy {
  readonly details$ = new BehaviorSubject<HighestDefectDetails>({
    label: 'No Defects',
    color: '#868e96',
  });

  readonly fetching$ = new BehaviorSubject<boolean>(false);

  private _onDefectsInitialisationSubscription?: Subscription;
  private _onViewDetailsChangedSubscription?: Subscription;

  constructor(
    private readonly _assets: AssetService,
    private readonly _view: ViewService,
    private readonly _translator: TranslateService,
  ) {}

  ngOnInit(): void {
    this._initDefects(data_get(this._view.getRecord(), 'record_details'));

    this._onViewDetailsChangedSubscription = this._view.details$.subscribe((details) => this._initDefects(details));
  }

  ngOnDestroy(): void {
    if (this._onViewDetailsChangedSubscription) {
      this._onViewDetailsChangedSubscription.unsubscribe();
    }

    if (this._onDefectsInitialisationSubscription) {
      this._onDefectsInitialisationSubscription.unsubscribe();
    }
  }

  onView = () => this._view.setTab('actual_faults');

  private _initDefects = (details: OptionalValue<ViewedRecordDetails>) => this._onDefectsInitialisationSubscription = observe({
    before: () => this.fetching$.next(true),
    after: () => this.fetching$.next(false),
    observable: () => defer(() => {
      if (blank(details)) {
        return EMPTY;
      }

      return this._assets.getFaults({
        id: details.id,
        sort_by_criticality: true,
        filter: 'unresolved',
      });
    })
  })
  .pipe(
    filter(filled)
  )
  .subscribe((faults: AssetActualFault[]) => {
    const highest = arr_first(faults);

    let label = spf(this._translator.instant('%d Defect'), {
      args: [1],
    });

    if (arr_count(faults) > 1) {
      label = spf(this._translator.instant('%d Defects'), {
        args: [arr_count(faults)]
      });
    }

    this.details$.next({
      label,
      color: highest.color,
    });
  });
}

type HighestDefectDetails = {
  label: string;
  color?: string;
}